// THE planner (one per user): dated meal entries, shopping schedule and trip days.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import { addDays } from '../logic/dates';
import { mergeTrips, shoppingDates } from '../logic/schedule';
import type { Day, Id, PlanEntry, Planner, ShoppingSchedule, Slot } from '../types';

type Actions = {
  plan: (date: Day, slot: Slot, recipeIds: Id[]) => void;
  addEntries: (entries: PlanEntry[]) => void;
  removeEntries: (ids: Id[]) => void;
  toggleTrip: (date: Day) => void;
  setSchedule: (schedule: ShoppingSchedule | null, today: Day) => void;
  /** On open (Swift PlannerView.onAppear): add scheduled trips, drop entries older than ~1 month. */
  housekeep: (today: Day) => void;
};

export const usePlanner = create<Planner & Actions>()(
  persist(
    set => ({
      entries: [],
      schedule: null,
      tripDates: [],
      updatedAt: 0,
      plan: (date, slot, recipeIds) =>
        set(s => ({
          entries: [...s.entries, ...recipeIds.map(recipeId => ({ id: nanoid(), date, slot, recipeId }))],
          updatedAt: Date.now(),
        })),
      addEntries: entries => set(s => ({ entries: [...s.entries, ...entries], updatedAt: Date.now() })),
      removeEntries: ids => set(s => ({ entries: s.entries.filter(e => !ids.includes(e.id)), updatedAt: Date.now() })),
      toggleTrip: date =>
        set(s => ({
          tripDates: s.tripDates.includes(date) ? s.tripDates.filter(d => d !== date) : mergeTrips(s.tripDates, [date]),
          updatedAt: Date.now(),
        })),
      setSchedule: (schedule, today) =>
        set(s => ({
          schedule,
          // Like Swift: saving generates the next 52 trips; turning off keeps existing ones.
          tripDates: schedule ? mergeTrips(s.tripDates, shoppingDates(schedule, today, 52)) : s.tripDates,
          updatedAt: Date.now(),
        })),
      housekeep: today =>
        set(s => {
          const cutoff = addDays(today, -31);
          return {
            entries: s.entries.filter(e => e.date >= cutoff),
            tripDates: s.schedule ? mergeTrips(s.tripDates, shoppingDates(s.schedule, today, 52)) : s.tripDates,
          };
        }),
    }),
    { name: 'planner', storage: persistStorage, version: 1, partialize: s => ({ entries: s.entries, schedule: s.schedule, tripDates: s.tripDates, updatedAt: s.updatedAt }) },
  ),
);
